/**
 * Card Brand and Type Detection Utilities
 * Detects card brand (Visa, Mastercard, etc.), type (Credit/Debit), and country from BIN
 * Uses BINlist API for real country detection
 */

/**
 * Detect card brand from card number (local detection)
 * @param {string} cardNumber - Card number or BIN
 * @returns {string} - Card brand name
 */
export const detectCardBrand = (cardNumber) => {
  if (!cardNumber) return 'Unknown';
  
  const cleaned = cardNumber.replace(/\s/g, '');
  
  // Visa
  if (/^4/.test(cleaned)) {
    return 'Visa';
  }
  
  // Mastercard
  if (/^(5[1-5]|2[2-7])/.test(cleaned)) {
    return 'Mastercard';
  }
  
  // American Express
  if (/^3[47]/.test(cleaned)) {
    return 'American Express';
  }
  
  // Discover
  if (/^(6011|65|64[4-9])/.test(cleaned)) {
    return 'Discover';
  }
  
  // Diners Club
  if (/^(36|38|30[0-5])/.test(cleaned)) {
    return 'Diners Club';
  }
  
  // JCB
  if (/^35/.test(cleaned)) {
    return 'JCB';
  }
  
  // Maestro
  if (/^(5018|5020|5038|5893|6304|6759|6761|6762|6763)/.test(cleaned)) {
    return 'Maestro';
  }
  
  // UnionPay
  if (/^62/.test(cleaned)) {
    return 'UnionPay';
  }
  
  // Elo
  if (/^(636368|438935|504175|451416|636297|5067|4576|4011)/.test(cleaned)) {
    return 'Elo';
  }
  
  // Hipercard
  if (/^(606282|3841)/.test(cleaned)) {
    return 'Hipercard';
  }
  
  return 'Unknown';
};

/**
 * Detect card type (Credit/Debit) based on BIN patterns (local detection)
 * @param {string} cardNumber - Card number or BIN
 * @returns {string} - Card type
 */
export const detectCardType = (cardNumber) => {
  if (!cardNumber) return 'Unknown';
  
  const cleaned = cardNumber.replace(/\s/g, '');
  const brand = detectCardBrand(cleaned);
  
  // Maestro is typically debit
  if (brand === 'Maestro') {
    return 'Debit';
  }
  
  // American Express is typically credit
  if (brand === 'American Express') {
    return 'Credit';
  }
  
  // For Visa and Mastercard, check specific BIN patterns
  // Visa Electron (debit)
  if (/^(4026|417500|4508|4844|4913|4917)/.test(cleaned)) {
    return 'Debit';
  }
  
  // Mastercard Maestro (debit)
  if (/^(5018|5020|5038|6304|6759|6761|6763)/.test(cleaned)) {
    return 'Debit';
  }
  
  // Default: Most cards are credit
  return 'Credit';
};

/**
 * Fetch real BIN information from BINlist API
 * @param {string} cardNumber - Card number or BIN
 * @returns {Promise<object>} - BIN information including country
 */
export const fetchBinInfo = async (cardNumber) => {
  if (!cardNumber) return null;
  
  try {
    const cleaned = cardNumber.replace(/\s/g, '');
    const bin = cleaned.substring(0, 6); // First 6 digits
    
    // Call BINlist API (free, no API key required)
    const response = await fetch(`https://lookup.binlist.net/${bin}`, {
      method: 'GET',
      headers: {
        'Accept-Version': '3'
      }
    });
    
    if (!response.ok) {
      return null;
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('BIN lookup error:', error);
    return null;
  }
};

/**
 * Get full country name from country code
 * @param {string} countryCode - ISO country code
 * @returns {string} - Full country name
 */
export const getCountryName = (countryCode) => {
  const countries = {
    'US': 'United States',
    'BR': 'Brazil',
    'CN': 'China',
    'JP': 'Japan',
    'GB': 'United Kingdom',
    'CA': 'Canada',
    'MX': 'Mexico',
    'DE': 'Germany',
    'FR': 'France',
    'IT': 'Italy',
    'ES': 'Spain',
    'IN': 'India',
    'AU': 'Australia',
    'RU': 'Russia',
    'AR': 'Argentina',
    'CL': 'Chile',
    'CO': 'Colombia',
    'PE': 'Peru',
    'VE': 'Venezuela',
    'NL': 'Netherlands',
    'BE': 'Belgium',
    'CH': 'Switzerland',
    'AT': 'Austria',
    'SE': 'Sweden',
    'NO': 'Norway',
    'DK': 'Denmark',
    'FI': 'Finland',
    'PL': 'Poland',
    'PT': 'Portugal',
    'GR': 'Greece',
    'TR': 'Turkey',
    'SA': 'Saudi Arabia',
    'AE': 'United Arab Emirates',
    'KR': 'South Korea',
    'TH': 'Thailand',
    'SG': 'Singapore',
    'MY': 'Malaysia',
    'ID': 'Indonesia',
    'PH': 'Philippines',
    'VN': 'Vietnam',
    'ZA': 'South Africa',
    'EG': 'Egypt',
    'NG': 'Nigeria',
    'KE': 'Kenya',
    'Unknown': 'Unknown'
  };
  
  return countries[countryCode] || countryCode;
};

/**
 * Get all card information at once with REAL API lookup
 * @param {string} cardNumber - Card number or BIN
 * @returns {Promise<object>} - Card information with real country
 */
export const getCardInfoAsync = async (cardNumber) => {
  // Try to get real data from API
  const binData = await fetchBinInfo(cardNumber);
  
  if (binData) {
    return {
      brand: binData.brand || detectCardBrand(cardNumber),
      type: binData.type || detectCardType(cardNumber),
      country: binData.country?.name || 'Unknown',
      countryCode: binData.country?.alpha2 || 'Unknown',
      bank: binData.bank?.name || 'Unknown',
      prepaid: binData.prepaid || false
    };
  }
  
  // Fallback to local detection if API fails
  return {
    brand: detectCardBrand(cardNumber),
    type: detectCardType(cardNumber),
    country: 'Unknown',
    countryCode: 'Unknown',
    bank: 'Unknown',
    prepaid: false
  };
};

/**
 * Get card info synchronously (local detection only - for immediate display)
 * @param {string} cardNumber - Card number or BIN
 * @returns {object} - Card information
 */
export const getCardInfo = (cardNumber) => {
  return {
    brand: detectCardBrand(cardNumber),
    type: detectCardType(cardNumber),
    country: 'Loading...',
    countryCode: 'Unknown',
    bank: 'Unknown'
  };
};
